import { useSyncExternalStore } from 'react'
import {
  advanceBoggleTime,
  applyBogglePlaygroundSettings,
  altClickSwap,
  beginSelection,
  clearSelection,
  clearSkillSelection,
  copyTile,
  createBoggleGameState,
  ctrlClickCopy,
  finishSelection,
  removeLastSelectionEntry,
  rotateBoard,
  resetBoggleGame,
  swapTile,
  submitPath,
  shouldAdvanceBoggleClock,
  tapSelection,
  toggleSkillMode,
  updateSelection,
  useActiveSkillOnTile,
  zapTile,
} from '~/boggle/engine/standard/game'
import {
  getBoggleSnapshot,
  renderBoggleGameToText,
} from '~/boggle/engine/standard/serialize'
import type {
  BoggleGameState,
  BogglePosition,
  BoggleResetOptions,
  BoggleRotationDirection,
  BoggleSkillMode,
} from '~/boggle/engine/standard/types'
import type { BogglePlaygroundSettings } from '~/boggle/config/playground'
import { getBoggleIntervalRemainingMs } from '~/boggle/lib/intervalTimer'
import { BOGGLE_GOLD_RUSH_BURST_ID } from '~/boggle/lib/meterBursts'
import {
  getBoggleAmbientPressureBand,
  type BoggleAmbientPressureBand,
} from '~/boggle/ui/chrome/boggleShellAmbientPolicy'

type Listener = () => void

type BoggleTimeState = {
  endIntervalOnSubmit: BoggleGameState['playgroundSettings']['endIntervalOnSubmit']
  intervalCount: BoggleGameState['playgroundSettings']['intervalCount']
  intervalDeadlineMs: BoggleGameState['intervalDeadlineMs']
  intervalDurationMs: BoggleGameState['intervalDurationMs']
  intervalPlayBeginsAtMs: BoggleGameState['intervalPlayBeginsAtMs']
  intervalsRemaining: BoggleGameState['intervalsRemaining']
  isInOvertime: BoggleGameState['isInOvertime']
  isPaused: boolean
  meter: BoggleGameState['meter']
  overtimeBackspaceCount: BoggleGameState['overtimeBackspaceCount']
  overtimePauseUntilMs: BoggleGameState['overtimePauseUntilMs']
  overtimeRemaining: BoggleGameState['overtimeRemaining']
  overtimeVelocity: BoggleGameState['overtimeVelocity']
  timeMs: BoggleGameState['timeMs']
}

type ListenerSet = Set<Listener>

const UI_EXCLUDED_KEYS: ReadonlySet<keyof BoggleGameState> = new Set([
  'allowDiagonals',
  'cols',
  'endReason',
  'feedback',
  'lastAction',
  'lastResolution',
  'lettersRepopulate',
  'meter',
  'nextCellId',
  'overtimeBackspaceCount',
  'overtimePauseUntilMs',
  'overtimeRemaining',
  'overtimeVelocity',
  'recentWords',
  'repeatTileMinGap',
  'rngState',
  'rows',
  'selectionMode',
  'timeMs',
  'totalLettersCleared',
  'wordsCleared',
])

function hasUiStateChanged(previousState: BoggleGameState, nextState: BoggleGameState) {
  for (const key in nextState) {
    if (UI_EXCLUDED_KEYS.has(key as keyof BoggleGameState)) {
      continue
    }
    if (
      !Object.is(
        previousState[key as keyof BoggleGameState],
        nextState[key as keyof BoggleGameState],
      )
    ) {
      return true
    }
  }
  return false
}

function createBoggleTimeState(
  state: BoggleGameState,
  isPaused: boolean,
): BoggleTimeState {
  return {
    endIntervalOnSubmit: state.playgroundSettings.endIntervalOnSubmit,
    intervalCount: state.playgroundSettings.intervalCount,
    intervalDeadlineMs: state.intervalDeadlineMs,
    intervalDurationMs: state.intervalDurationMs,
    intervalPlayBeginsAtMs: state.intervalPlayBeginsAtMs,
    intervalsRemaining: state.intervalsRemaining,
    isInOvertime: state.isInOvertime,
    isPaused,
    meter: state.meter,
    overtimeBackspaceCount: state.overtimeBackspaceCount,
    overtimePauseUntilMs: state.overtimePauseUntilMs,
    overtimeRemaining: state.overtimeRemaining,
    overtimeVelocity: state.overtimeVelocity,
    timeMs: state.timeMs,
  }
}

function subscribeToListeners(listeners: ListenerSet, listener: Listener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function notifyListeners(listeners: Iterable<Listener>) {
  for (const listener of listeners) {
    listener()
  }
}

export class BoggleStore {
  private listeners = new Set<Listener>()
  private uiListeners = new Set<Listener>()
  private timeListeners = new Set<Listener>()
  private state: BoggleGameState
  private uiState: BoggleGameState
  private timeState: BoggleTimeState
  private isPaused = false
  /**
   * False until the Pixi board has completed its first draw so wall-clock
   * (`useBoggleClock`) does not advance while fonts/init/heavy first paint run.
   */
  private boggleBoardSurfaceReady = false

  constructor(initialState = createBoggleGameState()) {
    this.state = initialState
    this.uiState = initialState
    this.timeState = createBoggleTimeState(initialState, this.isPaused)
  }

  setBoggleBoardSurfaceReady = (ready: boolean) => {
    this.boggleBoardSurfaceReady = ready
  }

  subscribe = (listener: Listener) => subscribeToListeners(this.listeners, listener)

  subscribeUi = (listener: Listener) => subscribeToListeners(this.uiListeners, listener)

  subscribeTime = (listener: Listener) =>
    subscribeToListeners(this.timeListeners, listener)

  getState = () => this.state
  getUiState = () => this.uiState
  getTimeState = () => this.timeState
  getPaused = () => this.isPaused

  getSnapshot = () => getBoggleSnapshot(this.state)

  renderGameToText = () => renderBoggleGameToText(this.state)

  pause = () => {
    this.setPaused(true)
  }

  resume = () => {
    this.setPaused(false)
  }

  togglePaused = () => {
    this.setPaused(!this.isPaused)
  }

  reset = (options: BoggleResetOptions = {}) => {
    this.isPaused = false
    this.commitState(resetBoggleGame(this.state, options))
    return this.getSnapshot()
  }

  advance = (ms: number) => {
    if (this.isPaused) {
      return this.getSnapshot()
    }

    this.commitState(advanceBoggleTime(this.state, ms))
    return this.getSnapshot()
  }

  setPlaygroundSettings = (settings: BogglePlaygroundSettings) => {
    this.commitStateIfChanged(applyBogglePlaygroundSettings(this.state, settings))
    return this.getSnapshot()
  }

  rotateBoard = (direction: BoggleRotationDirection) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(rotateBoard(this.state, direction))
    return this.getSnapshot()
  }

  beginSelection = (position: BogglePosition) => {
    if (this.isPlayerInputBlocked()) {
      return
    }

    this.commitState(beginSelection(this.state, position))
  }

  tapSelection = (position: BogglePosition) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(tapSelection(this.state, position))
    return this.getSnapshot()
  }

  toggleSkill = (mode: BoggleSkillMode) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(toggleSkillMode(this.state, mode))
    return this.getSnapshot()
  }

  clearSkillSelection = () => {
    if (this.isPlayerInputBlocked()) {
      return
    }

    this.commitStateIfChanged(clearSkillSelection(this.state))
  }

  clearSelection = () => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitStateIfChanged(clearSelection(this.state))
    return this.getSnapshot()
  }

  removeLastSelectionEntry = () => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(removeLastSelectionEntry(this.state))
    return this.getSnapshot()
  }

  updateSelection = (position: BogglePosition) => {
    if (this.isPlayerInputBlocked()) {
      return
    }

    const result = updateSelection(this.state, position)
    if (result.change.type !== 'ignored') {
      this.commitState(result.state)
      return
    }

    this.state = result.state
  }

  endSelection = () => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(finishSelection(this.state))
    return this.getSnapshot()
  }

  submitSelection = () => this.endSelection()

  submitPath = (positions: BogglePosition[]) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(submitPath(this.state, positions))
    return this.getSnapshot()
  }

  zapTile = (position: BogglePosition) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(zapTile(this.state, position))
    return this.getSnapshot()
  }

  useActiveSkillOnTile = (position: BogglePosition) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(useActiveSkillOnTile(this.state, position))
    return this.getSnapshot()
  }

  swapTile = (position: BogglePosition) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(swapTile(this.state, position))
    return this.getSnapshot()
  }

  copyTile = (position: BogglePosition) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(copyTile(this.state, position))
    return this.getSnapshot()
  }

  altClickSwap = (position: BogglePosition) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(altClickSwap(this.state, position))
    return this.getSnapshot()
  }

  ctrlClickCopy = (position: BogglePosition) => {
    if (this.isPlayerInputBlocked()) {
      return this.getSnapshot()
    }

    this.commitState(ctrlClickCopy(this.state, position))
    return this.getSnapshot()
  }

  hasTransientState = () =>
    (this.state.feedback !== null && this.state.feedback.untilMs !== null) ||
    this.state.lastResolution !== null

  shouldAdvanceClock = () =>
    !this.isPaused && this.boggleBoardSurfaceReady && shouldAdvanceBoggleClock(this.state)

  private isPlayerInputBlocked() {
    return this.isPaused && this.state.phase === 'playing'
  }

  private setPaused(paused: boolean) {
    const nextPaused = paused && this.state.phase === 'playing'
    if (nextPaused === this.isPaused) {
      return
    }

    this.isPaused = nextPaused
    this.emit()
  }

  private commitState(nextState: BoggleGameState) {
    if (nextState.phase !== 'playing') {
      this.isPaused = false
    }

    this.state = nextState
    this.emit()
  }

  private commitStateIfChanged(nextState: BoggleGameState) {
    if (nextState === this.state) {
      return
    }

    this.commitState(nextState)
  }

  private emit() {
    const nextTimeState = createBoggleTimeState(this.state, this.isPaused)
    const uiChanged = hasUiStateChanged(this.uiState, this.state)
    const timeChanged =
      !Object.is(this.timeState.endIntervalOnSubmit, nextTimeState.endIntervalOnSubmit) ||
      !Object.is(this.timeState.intervalCount, nextTimeState.intervalCount) ||
      !Object.is(this.timeState.intervalDeadlineMs, nextTimeState.intervalDeadlineMs) ||
      !Object.is(this.timeState.intervalDurationMs, nextTimeState.intervalDurationMs) ||
      !Object.is(
        this.timeState.intervalPlayBeginsAtMs,
        nextTimeState.intervalPlayBeginsAtMs,
      ) ||
      !Object.is(this.timeState.intervalsRemaining, nextTimeState.intervalsRemaining) ||
      !Object.is(this.timeState.isInOvertime, nextTimeState.isInOvertime) ||
      !Object.is(this.timeState.isPaused, nextTimeState.isPaused) ||
      !Object.is(this.timeState.meter, nextTimeState.meter) ||
      !Object.is(
        this.timeState.overtimeBackspaceCount,
        nextTimeState.overtimeBackspaceCount,
      ) ||
      !Object.is(
        this.timeState.overtimePauseUntilMs,
        nextTimeState.overtimePauseUntilMs,
      ) ||
      !Object.is(this.timeState.overtimeRemaining, nextTimeState.overtimeRemaining) ||
      !Object.is(this.timeState.overtimeVelocity, nextTimeState.overtimeVelocity) ||
      !Object.is(this.timeState.timeMs, nextTimeState.timeMs)

    this.uiState = uiChanged ? this.state : this.uiState
    this.timeState = timeChanged ? nextTimeState : this.timeState

    notifyListeners(this.listeners)

    if (uiChanged) {
      notifyListeners(this.uiListeners)
    }

    if (timeChanged) {
      notifyListeners(this.timeListeners)
    }
  }
}

export function useBoggleUiState(store: BoggleStore) {
  return useSyncExternalStore(store.subscribeUi, store.getUiState, store.getUiState)
}

export function useBoggleTimeState(store: BoggleStore) {
  return useSyncExternalStore(store.subscribeTime, store.getTimeState, store.getTimeState)
}

export function getBoggleIntervalRemainingMsFromTimeState(
  timeState: Pick<
    BoggleTimeState,
    | 'intervalDeadlineMs'
    | 'intervalDurationMs'
    | 'intervalPlayBeginsAtMs'
    | 'isInOvertime'
    | 'timeMs'
  >,
) {
  return getBoggleIntervalRemainingMs({
    intervalDeadlineMs: timeState.intervalDeadlineMs,
    intervalDurationMs: timeState.intervalDurationMs ?? 0,
    intervalPlayBeginsAtMs: timeState.intervalPlayBeginsAtMs,
    isInOvertime: timeState.isInOvertime,
    timeMs: timeState.timeMs,
  })
}

/** Interval ring remaining ms — updates on timer ticks; subscribe only from timer UI. */
export function useBoggleIntervalRemainingMs(store: BoggleStore) {
  const getSnapshot = () => getBoggleIntervalRemainingMsFromTimeState(store.getTimeState())

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

/**
 * Timer HUD display snapshot (4 Hz). Prefer imperative paint on `TimerGauge` with `store`;
 * kept for tests and any coarse read-only consumers.
 */
export const BOGGLE_TIMER_RING_DISPLAY_SNAPSHOT_MS = 250

export function quantizeBoggleTimerRingDisplayRemainingMs(remainingMs: number) {
  if (remainingMs <= 0) {
    return 0
  }

  return (
    Math.ceil(remainingMs / BOGGLE_TIMER_RING_DISPLAY_SNAPSHOT_MS) *
    BOGGLE_TIMER_RING_DISPLAY_SNAPSHOT_MS
  )
}

/** Coarse interval remaining for `TimerGauge` — 4 Hz, not sim tick rate. */
export function useBoggleIntervalRemainingDisplayMs(store: BoggleStore) {
  const getSnapshot = () =>
    quantizeBoggleTimerRingDisplayRemainingMs(
      getBoggleIntervalRemainingMsFromTimeState(store.getTimeState()),
    )

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

/** Ambient mood band — stable string snapshot; changes only at threshold crossings. */
export function useBoggleAmbientPressureBand(
  store: BoggleStore,
  showEndedSummary: boolean,
  summaryStandardOutcome?: null | 'new-best' | 'tie',
): BoggleAmbientPressureBand {
  const getSnapshot = () => {
    const timeState = store.getTimeState()
    return getBoggleAmbientPressureBand({
      showEndedSummary,
      summaryStandardOutcome,
      goldRushActive:
        timeState.meter.burstActive &&
        timeState.meter.activeBurstId === BOGGLE_GOLD_RUSH_BURST_ID,
      intervalDeadlineMs: timeState.intervalDeadlineMs,
      intervalDurationMs: timeState.intervalDurationMs,
      intervalPlayBeginsAtMs: timeState.intervalPlayBeginsAtMs,
      timeMs: timeState.timeMs,
    })
  }

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

/** Prep countdown active (`intervalPlayBeginsAtMs` set and clock still before play). */
export function useBoggleIntervalPrepActive(
  store: BoggleStore,
  intervalPlayBeginsAtMs: null | number,
) {
  const getSnapshot = () => {
    if (intervalPlayBeginsAtMs === null) {
      return false
    }
    return store.getTimeState().timeMs < intervalPlayBeginsAtMs
  }

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

export function useBoggleIsInOvertime(store: BoggleStore) {
  const getSnapshot = () => store.getTimeState().isInOvertime

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

export function useBoggleOvertimeRemaining(store: BoggleStore) {
  const getSnapshot = () => store.getTimeState().overtimeRemaining

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

/** Overtime bar display snapshot (10 Hz). Prefer imperative paint on `OvertimeBar`. */
export const BOGGLE_OVERTIME_BAR_DISPLAY_SNAPSHOT_S = 0.1

export function quantizeBoggleOvertimeBarDisplayRemaining(remaining: number) {
  return (
    Math.round(remaining / BOGGLE_OVERTIME_BAR_DISPLAY_SNAPSHOT_S) *
    BOGGLE_OVERTIME_BAR_DISPLAY_SNAPSHOT_S
  )
}

export function useBoggleOvertimeRemainingDisplay(store: BoggleStore) {
  const getSnapshot = () => {
    const remaining = store.getTimeState().overtimeRemaining
    return remaining === null ? null : quantizeBoggleOvertimeBarDisplayRemaining(remaining)
  }

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

export function useBoggleMeterValue(store: BoggleStore) {
  const getSnapshot = () => store.getTimeState().meter.value

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

/**
 * Meter arc display step (~capacity/80, minimum 1). Prefer imperative paint on `MeterGauge`
 * with `store`; kept for tests and coarse read-only consumers.
 */
export function getBoggleMeterDisplayQuantizeStep(capacity: number) {
  return capacity > 0 ? Math.max(1, Math.round(capacity / 80)) : 1
}

export function quantizeBoggleMeterDisplayValue(value: number, capacity: number) {
  if (value <= 0) {
    return 0
  }

  const step = getBoggleMeterDisplayQuantizeStep(capacity)
  return Math.round(value / step) * step
}

/** Coarse meter fill for `MeterGauge` — not sim tick rate. */
export function useBoggleMeterValueDisplay(store: BoggleStore, capacity: number) {
  const getSnapshot = () =>
    quantizeBoggleMeterDisplayValue(store.getTimeState().meter.value, capacity)

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

export function useBoggleMeterOvermeterValueDisplay(store: BoggleStore, capacity: number) {
  const getSnapshot = () =>
    quantizeBoggleMeterDisplayValue(store.getTimeState().meter.overmeterValue, capacity)

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

/** Threshold boolean — stable except when meter crosses capacity. */
export function useBoggleMeterAtOrAboveCapacity(store: BoggleStore, capacity: number) {
  const getSnapshot = () => store.getTimeState().meter.value >= capacity

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

export function useBoggleMeterGoldRushActive(store: BoggleStore) {
  const getSnapshot = () => {
    const meter = store.getTimeState().meter
    return meter.burstActive && meter.activeBurstId === BOGGLE_GOLD_RUSH_BURST_ID
  }

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

/** True when any meter burst is active (not necessarily Gold Rush). */
export function useBoggleMeterBurstActive(store: BoggleStore) {
  const getSnapshot = () => store.getTimeState().meter.burstActive

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

export function useBoggleMeterOvermeterValue(store: BoggleStore) {
  const getSnapshot = () => store.getTimeState().meter.overmeterValue

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}

/** Pause flag only (boolean snapshot) — does not re-render on each timer tick. */
export function useBogglePlayingPhasePaused(
  store: BoggleStore,
  phase: BoggleGameState['phase'],
) {
  const getPausedSnapshot = () =>
    phase === 'playing' ? store.getTimeState().isPaused : false

  return useSyncExternalStore(store.subscribeTime, getPausedSnapshot, getPausedSnapshot)
}
